<template>
    <div v-if="isLoadingSomething">
        <font-awesome-icon icon="spinner" spin/>
    </div>
    <div v-else>
        <div v-if="copyFromOtherTypeProperty" class="form-check mb-4">
            <b-checkbox v-model="copyFromOtherTypeProperty.value" switch inline id="checkboxCopy" :disabled="!copyFromOtherTypeProperty.can_edit"
                        class="pt-2 mr-1"
            />
            <label for="checkboxCopy" class="form-check-label" style="line-height:200%;">{{ copyFromOtherTypeProperty.key.display_name }}</label>

            <OmniButton
                v-if="serviceType.can_edit"
                :disabled="formHasInvalidServiceTime || isSaving['serviceTimes']"
                @click="save"
                variant="success"
                class="float-right"
                :isProcessing="isSaving['serviceTimes']"
            >
                {{ $t('omt.gui.general.action.save') }}
            </OmniButton>
        </div>

        <div>
            <form @submit.prevent="save" autocomplete="off">
                <div class="row mb-3">
                    <div class="col-8">
                        <h3>{{ serviceType.display_name }}</h3>
                    </div>

                    <div v-if="!copyFromOtherTypeProperty" class="col-4 text-right">
                        <OmniButton
                            v-if="serviceType.can_edit"
                            :disabled="formHasInvalidServiceTime || isSaving['serviceTimes']"
                            @click="save"
                            variant="success"
                            class="float-right"
                            :isProcessing="isSaving['serviceTimes']"
                        >
                            {{ $t('omt.gui.general.action.save') }}
                        </OmniButton>
                    </div>
                </div>

                <b-row v-if="formHasChanges && !formHasInvalidServiceTime">
                    <b-col>
                        <OmniAlertInline class="mb-4" variant="warning" size="sm">
                            <span v-if="formHasChanges">Nog niet alle wijzigingen zijn opgeslagen.</span>
                            <span v-else>
                                Een moment geduld. De wijzigingen worden opgeslagen.
                                <FontAwesomeIcon icon="spinner" spin />
                            </span>
                        </OmniAlertInline>
                    </b-col>
                </b-row>
            </form>
            <b-card>
                <b-card-title>
                    <div class="row mt-2">
                        <div class="col-12 font-weight-bold mb-3">
                            {{ $t('omt.gui.location-management.service-times.different-times') }}
                        </div>
                    </div>
                </b-card-title>
                <form @submit.prevent="save" autocomplete="off">
                    <div class="row my-2">
                        <div class="col">{{ $t('omt.gui.location-management.service-times.date') }}</div>
                        <div class="col">{{ $t('omt.gui.location-management.service-times.status') }}</div>
                        <div v-if="!useCutOff" class="col">{{ $t('omt.gui.location-management.service-times.from') }}</div>
                        <div v-if="!useCutOff" class="col">{{ $t('omt.gui.location-management.service-times.to') }}</div>
                        <div v-if="useCutOff" class="col">{{ $t('omt.gui.location-management.service-times.cutoff') }}</div>
                        <div class="col">{{ $t('omt.gui.location-management.service-times.description') }}</div>
                        <div v-if="usesCapacities(serviceType)" class="col">{{ $t('omt.gui.location-management.service-times.max-capacity') }}</div>
                        <div class="col"></div>
                    </div>
                    <div class="my-1" v-for="(serviceTimeId) in sortedServiceTimesByDateIds" :key="serviceTimeId">
                        <LocationServiceTimeForm
                            v-model="locationServiceTimes[serviceTimeId]"
                            :date="true"
                            :use-cut-off="!!useCutOff"
                            :uses-capacities="!!usesCapacities(serviceType)"
                            :read-only="isReadOnlyLocationServiceType(serviceType)"
                            @validated="(...args) => setServiceTimeValidated(serviceTimeId, args[0])"
                            @serviceTimeUpdated="locationServiceTimeUpdated(locationServiceTimes[serviceTimeId])"
                        >
                            <OmniButton
                                v-if="serviceType.can_edit && !isCopyFromOpenTimesEnabled"
                                @click="removeServiceTime(serviceTimeId)"
                                variant="danger"
                                icon="times"
                                style="width: 100%;"
                            >
                                Verwijderen
                            </OmniButton>
                        </LocationServiceTimeForm>
                    </div>
                </form>

                <form v-if="serviceType.can_edit" @submit.prevent="addServiceTime(newServiceTimeByDate)" autocomplete="off">
                    <template v-if="syncOpeningTimesEnabled">
                        <div class="row my-1">
                            <div class="col-12 py-2">
                                <i>{{ $t('omt.gui.location-management.service-times.sync-enabled') }}</i>
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <LocationServiceTimeForm v-model="newServiceTimeByDate"
                                                 :date="true"
                                                 :create="true"
                                                 :use-cut-off="!!useCutOff"
                                                 :uses-capacities="!!usesCapacities(serviceType)"
                                                 @validated="(...args) => this.isValidNewServiceTimeDate = args[0]"
                                                 @serviceTimeUpdated="locationServiceTimeUpdated(newServiceTimeByDate)"
                        >
                            <OmniButton
                                :disabled="!isValidNewServiceTimeDate || this.newServiceTimeByDate.is_available.toString() === ''"
                                :class="{
                                        'btn-outline-secondary': !isValidNewServiceTimeDate || this.newServiceTimeByDate.is_available.toString() === '',
                                        'btn-success': isValidNewServiceTimeDate,
                                        }"
                                class="btn btn-sm"
                                icon="plus"
                                type="submit"
                                style="width: 100%;"
                            >
                                Toevoegen
                            </OmniButton>
                        </LocationServiceTimeForm>
                    </template>
                </form>
            </b-card>

            <b-card class="mt-4">
                <b-card-title>
                    <div class="row">
                        <div class="col-12 font-weight-bold mb-3">
                            {{ $t('omt.gui.location-management.service-times.regular-times') }}
                        </div>
                    </div>
                </b-card-title>

                <form @submit.prevent="save" autocomplete="off">
                    <div class="row my-2">
                        <div class="col">{{ $t('omt.gui.location-management.service-times.date') }}</div>
                        <div class="col">{{ $t('omt.gui.location-management.service-times.status') }}</div>
                        <div v-if="!useCutOff" class="col">{{ $t('omt.gui.location-management.service-times.from') }}</div>
                        <div v-if="!useCutOff" class="col">{{ $t('omt.gui.location-management.service-times.to') }}</div>
                        <div v-if="useCutOff" class="col">{{ $t('omt.gui.location-management.service-times.cutoff') }}</div>
                        <div class="col">{{ $t('omt.gui.location-management.service-times.description') }}</div>
                        <div v-if="usesCapacities(serviceType)" class="col">{{ $t('omt.gui.location-management.service-times.max-capacity') }}</div>
                        <div class="col"></div>
                    </div>
                    <div class="my-1" v-for="(serviceTimeId) in sortedServiceTimesByWeekDayIds" :key="serviceTimeId">
                        <LocationServiceTimeForm v-model="locationServiceTimes[serviceTimeId]"
                                                 :use-cut-off="!!useCutOff"
                                                 :uses-capacities="!!usesCapacities(serviceType)"
                                                 :read-only="isReadOnlyLocationServiceType(serviceType)"
                                                 @validated="(...args) => setServiceTimeValidated(serviceTimeId, args[0])"
                                                 @serviceTimeUpdated="locationServiceTimeUpdated(locationServiceTimes[serviceTimeId])">
                            <OmniButton
                                v-if="serviceType.can_edit && !isCopyFromOpenTimesEnabled"
                                @click="removeServiceTime(serviceTimeId)"
                                variant="danger"
                                size="sm"
                                icon="times"
                                style="width: 100%;"
                            >
                                Verwijderen
                            </OmniButton>
                        </LocationServiceTimeForm>
                    </div>
                </form>
                <form v-if="serviceType.can_edit" @submit.prevent="addServiceTime(newServiceTimeByWeekDay)" autocomplete="off">
                    <template v-if="syncOpeningTimesEnabled">
                        <div class="row my-1" v-if="syncOpeningTimesEnabled">
                            <div class="col-12 py-2">
                                <i>{{ $t('omt.gui.location-management.service-times.sync-enabled') }}</i>
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <LocationServiceTimeForm v-model="newServiceTimeByWeekDay"
                                                 :create="true"
                                                 :use-cut-off="!!useCutOff"
                                                 :uses-capacities="!!usesCapacities(serviceType)"
                                                 @validated="(...args) => this.isValidNewServiceTimeWeekDay = args[0]"
                                                 @serviceTimeUpdated="locationServiceTimeUpdated(newServiceTimeByWeekDay)">
                            <OmniButton type="submit"
                                        :disabled="!isValidNewServiceTimeWeekDay || this.newServiceTimeByWeekDay.is_available.toString() === ''"
                                        :class="{
                                    'btn-outline-secondary': !isValidNewServiceTimeWeekDay || this.newServiceTimeByWeekDay.is_available.toString() === '',
                                    'btn-primary': isValidNewServiceTimeWeekDay,
                                    }"
                                        size="sm"
                                        icon="plus"
                                        style="width: 100%;"
                            >
                                Toevoegen
                            </OmniButton>
                        </LocationServiceTimeForm>
                    </template>
                </form>
            </b-card>

            <b-card v-if="hasDaysToProcess" class="mt-4">
                <b-card-title>
                    <div class="row">
                        <div class="col-12 font-weight-bold mb-3">
                            {{ $t('omt.gui.location-management.service-times.days-to-process') }}
                        </div>
                    </div>
                </b-card-title>
                <form @submit.prevent="save">
                    <div class="row my-4" v-for="value in newDaysToProcess">
                        <div class="col-4">
                            <div class="row">
                                <div class="col-10">{{ value.key.display_name }}</div>
                                <div class="col-2 pr-0 pl-1">
                                    <input v-model="value.value" type="number" class="form-control form-control-sm" :readonly="!value.can_edit">
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </b-card>
            <div class="form-check mt-4">
                <OmniButton
                    v-if="serviceType.can_edit"
                    :disabled="formHasInvalidServiceTime || isSaving['serviceTimes']"
                    @click="save"
                    :isProcessing="isSaving['serviceTimes']"
                    variant="success" class="float-right"
                >
                    {{ $t('omt.gui.general.action.save') }}
                </OmniButton>
            </div>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import LocationServiceTimeForm from "./Forms/LocationServiceTimeForm";
import {WEEKDAYS, AVAILABLE_STATUS} from "../../helpers/consts";

const SERVICE_TYPE_PROCESSING_DAYS_KEYS = {
    'ship_from_store_processing': ['shipFromStoreDaysToProcess'],
    'inter_store_processing': ['interStoreDaysToProcessTbvAfhalen', 'interStoreDaysToProcessTbvBezorgen'],
    'dropship_processing': ['dropshipDaysToProcess'],
    'crossdock_processing': ['crossdockDaysToProcessTbvAfhalen', 'crossdockDaysToProcessTbvBezorgen'],
    'pickup': ['pickupDaysToProcess'],
}

export default {
    name: "EditServiceTimesForType",

    components: {
        LocationServiceTimeForm
    },
    components: {
        LocationServiceTimeForm
    },

    props: {
        serviceType: {required: true, type: Object},
        location: {required: true, type: Object},
        useCutOff: {required: true, type: Boolean},
    },

    data() {
        return {
            isLoading: {
                servicesTimes: true,
                syncStatus: true,
            },
            isSaving: {
                serviceTimes: false,
            },
            locationServiceTimes: {},
            locationServiceTimesOriginal: {},
            locationProperties: [],
            locationServiceTimesValidated: {},
            newServiceTimeByDate: {
                date: '',
                week_day: '',
                is_available: '',
                from_time: '',
                to_time: '',
                max_capacity: null,
                description: '',
            },
            newServiceTimeByWeekDay: {
                date: '',
                week_day: '',
                is_available: '',
                from_time: '',
                to_time: '',
                max_capacity: null,
                description: '',
            },
            newDaysToProcess: [],
            newDaysToProcessOriginal: [],
            isValidNewServiceTimeWeekDay: false,
            isValidNewServiceTimeDate: false,
            copyFromOtherTypePropertyInitialValue: false,
        }
    },

    computed: {
        isLoadingSomething() {
            return Object.values(this.isLoading).some(value => (value === true));
        },
        weekdays() {
            return WEEKDAYS
        },
        availableStatus() {
            return AVAILABLE_STATUS
        },
        sortedServiceTimes() {
            return Object.values(this.locationServiceTimes).sort(this.sortServiceTimes)
        },
        sortedServiceTimesByWeekDayIds() {
            return this.sortedServiceTimes
                .filter(time => !time.date)
                .map(time => time.id)
        },
        sortedServiceTimesByDateIds() {
            return this.sortedServiceTimes
                .filter(time => !time.week_day)
                .map(time => time.id)
        },
        weekDayIndex() {
            return Object.keys(WEEKDAYS)
        },
        copyFromOtherTypeProperty() {
            let copyProperty = this.locationProperties
                .filter(prop => prop.key.identifier.startsWith('syncServiceTimes.') && prop.key.identifier.endsWith(`.${this.serviceType.identifier}`))
                [0]

            return (typeof copyProperty !== 'undefined')
                ? copyProperty
                : null
        },
        isCopyFromOpenTimesEnabled() {
            return (this.copyFromOtherTypeProperty)
                ? !!this.copyFromOtherTypeProperty.value
                : false
        },
        formHasInvalidServiceTime() {
            return !!Object.values(this.locationServiceTimesValidated)
                .filter(isValid => !isValid)
                .length
        },
        syncOpeningTimesEnabled() {
            return this.isCopyFromOpenTimesEnabled
        },
        hasDaysToProcess() {
            return this.serviceType.identifier in SERVICE_TYPE_PROCESSING_DAYS_KEYS
        },
        formHasChanges() {
            return this.formHasServiceTimesChanges
                || !_.isEqual(this.newDaysToProcess, this.newDaysToProcessOriginal)
        },
        formHasServiceTimesChanges() {
            return !_.isEqual(this.locationServiceTimes, this.locationServiceTimesOriginal)
        },
    },

    mounted() {
        const promises = [
            this.getServiceTimes()
                .then(() => this.isLoading['servicesTimes'] = false),
            this.loadProperties()
                .then(() => {
                    this.newDaysToProcess = this.findDaysToProcessProperties()
                    this.isLoading['syncStatus'] = false
                    this.copyFromOtherTypePropertyInitialValue = this.copyFromOtherTypeProperty?.value
                }),
        ]

        Promise.all(promises)
            .then(() => {
                this.syncOriginalState();
            })
    },

    methods: {
        /**
         * Creates a copy of locationServicesTimes and keeps it in sync with locationServicesTimesOriginal
         * so that the computed property formHasChanges() knows that there are no more changes.
         *
         * Call this method after retrieving the data for the first time and after sucessfully saving the changes.
         */
        syncOriginalState() {
            this.locationServiceTimesOriginal = structuredClone(this.locationServiceTimes);
            this.newDaysToProcessOriginal = structuredClone(this.newDaysToProcess);
        },
        async getServiceTimes() {
            return this.$http.get(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/service-times/${this.serviceType.identifier}`)
                .then(response => {
                    this.locationServiceTimes = _.keyBy(response.data, 'id');
                })
        },
        loadProperties() {
            return this.$http.get(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/properties`)
                .then(response => {
                    this.locationProperties = response.data.properties
                })
        },
        checkValidServiceTime(serviceTime) {
            if (serviceTime.date && !this.isValidNewServiceTimeDate) {
                return false
            }
            if (serviceTime.week_day && !this.isValidNewServiceTimeWeekDay) {
                return false
            }

            return true
        },
        addServiceTime(serviceTime) {
            if (!this.checkValidServiceTime(serviceTime)) {
                return
            }

            if (serviceTime.week_day && this.isWeekdayAlreadyPresent(serviceTime.week_day)) {
                alert(`Er kan maar één tijd per weekdag worden ingesteld.`)
                return
            }

            if (serviceTime.date && this.isDateAlreadyPresent(serviceTime.date)) {
                alert(`Er kan maar één tijd per datum worden ingesteld.`)
                return
            }

            let serviceTimeToAddIndex = (new Date()).getTime()
            let serviceTimeToAdd = _.clone(serviceTime)
            serviceTimeToAdd.id = serviceTimeToAddIndex
            serviceTimeToAdd.location_service_type = {identifier: this.serviceType.identifier}

            this.$set(this.locationServiceTimes, serviceTimeToAddIndex, serviceTimeToAdd)
        },
        removeServiceTime(id) {
            if (!confirm('Weet je zeker dat je deze regel wilt verwijderen?'))
                return

            this.$delete(this.locationServiceTimes, id);
        },
        async save() {
            try {
                if (this.formHasInvalidServiceTime) {
                    return
                }

                this.isSaving['serviceTimes'] = true
                await this.saveCopyFromTypeStatus()
                let promises = []

                // Deze niet als laatste uitvoeren. Het gaat het mis met de publish vlag als een gebruiker de permissie "locations.properties.edit" niet heeft
                promises.push(async (publish) => await this.saveDaysToProcess(publish))

                if (this.copyFromOtherTypeProperty?.value && this.usesCapacities(this.serviceType)) {
                    // If the sync is enabled, we should use a PATCH request and only update specific fields (i.e. the max_capacity field) of the serviceType.
                    const endpoint = `/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/service-times/${this.serviceType.identifier}`;
                    const patchData = {
                        service_times: Object.values(this.locationServiceTimes)
                            .map(locationServiceTime => ({
                                id: locationServiceTime.id,
                                max_capacity: locationServiceTime.max_capacity,
                            }))
                    }

                    promises.push(async (publish) => await this.$http.patch(`${endpoint}?publish=${publish}`, patchData))
                } else if (!this.copyFromOtherTypeProperty?.value && this.formHasServiceTimesChanges) {
                    // Save the service times
                    let postData = {
                        service_times: Object.values(this.locationServiceTimes)
                            .sort(this.sortServiceTimes)
                            .map((serviceTime, index) => {
                                if (!serviceTime.location_service_type_identifier) {
                                    serviceTime.location_service_type_identifier = this.serviceType.identifier
                                }
                                serviceTime.date = (serviceTime.date) ? serviceTime.date : null
                                serviceTime.week_day = (serviceTime.week_day) ? serviceTime.week_day : null
                                serviceTime.priority = index

                                if (serviceTime.from_time) {
                                    serviceTime.from_time = serviceTime.from_time.replace(/^([0-9]{1,2})[:\.]([0-9]{1,2})$/, '$1:$2')
                                }
                                if (serviceTime.to_time) {
                                    serviceTime.to_time = serviceTime.to_time.replace(/^([0-9]{1,2})[:\.]([0-9]{1,2})$/, '$1:$2')
                                }

                                return serviceTime
                            })
                    }

                    promises.push(async (publish) => await this.$http.put(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/service-times/${this.serviceType.identifier}?publish=${publish}`, postData))
                }

                for (let i = 0; i < promises.length; i++) {
                    await promises[i](i === (promises.length - 1)) // The last promise should have publish=true
                }

                this.$alert('Opgeslagen')

            } catch (e) {
                console.error(e)
                this.$alert('Er is iets misgegaan. Mogelijk is niet alles goed opgeslagen.', 'danger')
            } finally {
                this.isSaving['serviceTimes'] = false;
                this.syncOriginalState();
            }
        },
        async saveCopyFromTypeStatus() {
            if (!this.copyFromOtherTypeProperty || !this.copyFromOtherTypeProperty.can_edit) {
                return Promise.resolve()
            }

            /**
             * Skip als de sync. vlag niet is gewijzigd, anders krijg je de capaciteit instellingen
             * te zien omdat die update hierna gebeurt.
             */
            if (this.copyFromOtherTypePropertyInitialValue === this.copyFromOtherTypeProperty.value) {
                return Promise.resolve()
            }
            this.copyFromOtherTypePropertyInitialValue = this.copyFromOtherTypeProperty.value

            let postData = {
                value: this.copyFromOtherTypeProperty.value === true,
            }

            await this.$http.patch(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/properties/${this.copyFromOtherTypeProperty.key.identifier}?publish=0`, postData)

            if (this.copyFromOtherTypeProperty.value) {
                // Do not publish here, because the publishing will be done when the capacities are saved, or when the days to process. One of these will always be called
                await this.$http.post(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/sync-service-times?publish=0`)
                await this.getServiceTimes()
                this.$alert('Synchronisatie staat aan')
            }

            return Promise.resolve()
        },
        async saveDaysToProcess(publish = 0) {
            if (!this.hasPermission('locations.properties.edit')) return Promise.resolve();

            this.setDaysToProcessProperties()

            const postData = {
                properties: _.cloneDeep(this.locationProperties)
                    .filter(property => property.value !== '')
                    .map(property => {
                        property.key = property.key.identifier

                        if (this.hasDaysToProcess && SERVICE_TYPE_PROCESSING_DAYS_KEYS[this.serviceType.identifier].includes(property.key)) {
                            property.value = this.newDaysToProcess[property.key] !== null ? parseInt(property.value) : null
                        }

                        return property
                    }),
            }

            return this.$http.patch(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/properties?publish=${publish}`, postData)
        },
        sortServiceTimes(a, b) {
            if (a.date > b.date) return -1
            if (a.date < b.date) return 1
            if (this.weekDayIndex.indexOf(a.week_day) > this.weekDayIndex.indexOf(b.week_day)) return 1
            if (this.weekDayIndex.indexOf(a.week_day) < this.weekDayIndex.indexOf(b.week_day)) return -1
            if (a.is_available > b.is_available) return -1
            if (a.is_available < b.is_available) return 1
            if (a.from_time > b.from_time) return 1
            if (a.from_time < b.from_time) return -1
            if (a.to_time > b.to_time) return 1
            if (a.to_time < b.to_time) return -1
            return 0
        },
        isWeekdayAlreadyPresent(weekDay) {
            return !!Object.values(this.locationServiceTimes)
                .filter(time => time.location_service_type.identifier === this.serviceType.identifier && time.week_day === weekDay)
                .length
        },
        isDateAlreadyPresent(date) {
            return !!Object.values(this.locationServiceTimes)
                .filter(time => time.location_service_type.identifier === this.serviceType.identifier && time.date === date)
                .length
        },
        setServiceTimeValidated(serviceTimeId, isValid) {
            this.$set(this.locationServiceTimesValidated, serviceTimeId, isValid);
        },
        locationServiceTimeUpdated(form) {
            // this.$nextTick prevents a race condition where the value is not actually updated yet but the @input was triggered already
            this.$nextTick(() => {
                if (this.useCutOff && form.is_available.toString() === '0') {
                    form.to_time = ''
                }
            })
        },
        usesCapacities(locationServiceType) {
            return locationServiceType.uses_capacities && locationServiceType.uses_capacities === 1
        },
        isReadOnlyLocationServiceType(locationServiceType) {
            let returnValue = true;

            if (locationServiceType.can_edit)     returnValue = false;
            if (this.isCopyFromOpenTimesEnabled)  returnValue = true;

            return returnValue;
        },
        setDaysToProcessProperties() {
            if (!this.hasDaysToProcess) {
                return
            }

            this.locationProperties = this.locationProperties.map((property) =>  {
                if (SERVICE_TYPE_PROCESSING_DAYS_KEYS[this.serviceType.identifier].includes(property.key.identifier)) {
                    property = this.newDaysToProcess.filter(newProperty => newProperty.key.identifier === property.key.identifier)[0]
                }

                return property
            })
        },
        findDaysToProcessProperties() {
            if (!this.hasDaysToProcess) {
                return [];
            }

            return this.locationProperties.filter(property => {
                return SERVICE_TYPE_PROCESSING_DAYS_KEYS[this.serviceType.identifier].includes(property.key.identifier)
            })
        },
    },
    watch: {
        formHasChanges(newVal) {
            this.$root.hasUnsavedChanges = newVal;
        },
    }
}
</script>

<style scoped lang="scss">
input.invalid {
    border-color: red !important;
}
</style>
